import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { fadeIn, fadeOut } from 'src/app/animations';
import { BinderWizardDetails } from 'src/app/models/binder/binder-wizard-details.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { BinderWizardService } from 'src/app/services/sections/binder/binder-wizard.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { NavigationActionButton } from '../navigation-bar/models/navigation-action-button.model';
import { BinderUserAccessAccount } from 'src/app/models/binder/binder-user-access-account.model';
import { Location } from '@angular/common';
import { LocationService } from 'src/app/services/location.service';
import { takeUntil } from 'rxjs/operators';
import { number } from 'prop-types';
import { BinderSessionViewByEmail } from 'src/app/models/binder/binder-session-view.model';

enum SectionsEnum { 
	Error = 3 as number,
	BinderSessions = 6 as number,
}

@Component({
	selector: 'virtual-binder-wizard',
	templateUrl: './virtual-binder-wizard.component.html',
	styleUrls: ['./virtual-binder-wizard.component.scss', '../stylesheets/job-actions.scss', '../stylesheets/job-main.scss', '../stylesheets/job-inputs.scss', '../stylesheets/forms-container.scss'],
	animations: [fadeIn(200), fadeOut(200)]
})
export class VirtualBinderWizardComponent implements OnInit, AfterViewInit, OnDestroy {
	public sections = [
		true,
		false,
		false,
		false, // Error
		false,
		false,
		false, // BinderSessions
	];
	public SectionsEnum = SectionsEnum;
	public isDataLoading = false;
	public isEmailCached: boolean;
	public userAccount: BinderUserAccessAccount;

	public binderWizardDetails: BinderWizardDetails = null;

	public binderFormGroup: FormGroup;

	public isBinderEmailAllowed: boolean;
	public errorMessage: string;
	public publicViewingMessage: string;
	public publicAccessWarningMessage = 'Current Binder does not allow public access. If you need to access, please contact Customer Support at Vitralogy for more information.';
	public noPublicAccessMessage = 'Current Binder only allows public access to registered domains. Please contact Customer Support at Vitralogy for more information.';
	public autoRegisterWarningMessage = 'Your account does not have the proper permission for this Binder. Please contact Customer Support at Vitralogy for more information.';

	public hasGeoFencingTokenDisabled: boolean = false;
	public accessToken = '';
	public isGeoFencingTokenValid = false;
	public isGeoFencingValidationFailed = false;
	public isTokenEnabledFlow = false;
	public binderSessions: BinderSessionViewByEmail[] = [];

	public get currentlyVisibleSection(): number {
		for (let index = this.sections.length - 1; index >= 0; --index) {
			if (this.sections[index]) {
				return index;
			}
		}
	}

	constructor(
		private activatedRoute: ActivatedRoute,
		private binderWizardService: BinderWizardService,
		private router: Router,
		private location: Location,
		private locationService: LocationService
	) { }

	ngOnInit(): void {
		GlobalSettingsService.priorityNavigationBarActions = new Map<string, NavigationActionButton>();

		this.isEmailCached = !!localStorage.getItem('cloud-email');

		this.binderWizardDetails = this.activatedRoute.snapshot.data.binderWizardDetails;

		this.binderFormGroup = new FormGroup({
			email: new FormControl(this.isEmailCached ? localStorage.getItem('cloud-email') : '', [Validators.required, Validators.email, Validators.minLength(3), Validators.pattern(/\S+/)]),
			hasWorkToDo: new FormControl(false)
		});

		this.binderFormGroup.get('email').valueChanges.subscribe(
			value => {
				this.checkCrtEmail();
			}
		);

		const state: any = this.location.getState();
		if (state && state.selectedBinder) {
			this.binderFormGroup.patchValue({ email: state.email, hasWorkToDo: state.hasWorkToDo });
			this.binderWizardDetails.hasEnhancedSecurity = state.selectedBinder.hasEnhancedSecurity;
			this.binderWizardDetails.isGeoFencingRequired = state.selectedBinder.isGeoFencingRequired;
			this.binderWizardDetails.accountSiteName = state.site;
			this.binderWizardDetails.defaultGeoFencingRadius = state.selectedBinder.defaultGeoFencingRadius;
			this.binderWizardDetails.latitude = state.latitude;
			this.binderWizardDetails.longitude = state.longitude;
			this.binderWizardDetails.hasCustomerSupportTokenIssuer = state.selectedBinder.hasCustomerSupportTokenIssuer;
			this.binderWizardDetails.tokenAuthOnlyOnFailedGeoFencing = state.selectedBinder.tokenAuthOnlyOnFailedGeoFencing;
			this.isBinderAndCustomerLegacy();
		}
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			GlobalSettingsService.isGlobalLoaderLoading = false;
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		}, 0);
	}

	ngOnDestroy(): void {
		GlobalSettingsService.priorityNavigationBarActions = null;
	}

	keyPressed($event: any) {
		if ($event.keyCode === 13 /*Enter */) {
			this.getUserAccountsData();
		}
	}

	checkCrtEmail() {
		const model = this.binderFormGroup.getRawValue();
		if (model.email.indexOf(' ') >= 0) {
			this.binderFormGroup.patchValue({ email: model.email.replaceAll(' ', '') });
		}
	}

	public isBinderAndCustomerLegacy(): void {
		const model = this.binderFormGroup.getRawValue();
		this.isBinderEmailAllowed = false;
		this.isDataLoading = true;
		this.binderWizardService
			.isBinderEmailAllowed(TokenHelper.getToken(this.activatedRoute), model.email)
			.subscribe(
				res => {
					this.isBinderEmailAllowed = res;
				},
				err => this.isDataLoading = false,
				() => {
					if (this.binderWizardDetails.isBinderAndCustomerLegacy) {
						this.getUserAccountsData();
					} else {
						if (model.hasWorkToDo) {
							this.hasWorkToDoFlow();
						} else {
							if (!this.binderWizardDetails.isLimitedPublicView 
								|| !this.binderWizardDetails.limitAccessToCustomerWhitelist 
								|| (this.binderWizardDetails.limitAccessToCustomerWhitelist 
									&& this.isBinderEmailAllowed)) {
								this.getUserAccountsData();
							} else {
								if (!this.binderWizardDetails.limitAccessToCustomerWhitelist) {
									this.publicViewingMessage = 'DENIED';
									this.errorMessage = this.publicAccessWarningMessage;
								} else {
									this.errorMessage = this.noPublicAccessMessage;
									this.publicViewingMessage = 'FAILED';
								}
								this.jumpToSection(SectionsEnum.Error);
								this.isDataLoading = false;
							}
						}
					}
				});
	}

	public getUserAccountsData(): void {
		const model = this.binderFormGroup.getRawValue();

		this.isDataLoading = true;

		this.binderWizardService.getBinderUserAccessData(TokenHelper.getToken(this.activatedRoute), model.email).subscribe(res => {
			this.userAccount = res;
			if (!!this.userAccount) {
				console.log('Account:', this.userAccount.binderPermissionAccountTypeName);
				this.submitBinderQuery(this.userAccount);
			}
			else {
				console.log('Account: Nobody');
				if (!this.binderFormGroup.get('hasWorkToDo').value)
				{
					this.binderWizardService.getBinderSessions(
						TokenHelper.getToken(this.activatedRoute), model.email)
						.subscribe(res => {
							this.binderSessions = res;
							if (this.binderSessions.length > 1) {
								this.jumpToSection(SectionsEnum.BinderSessions);
								this.isDataLoading = false;
							} else if (this.binderSessions.length === 1) {
								this.NavigateToBinderSession(this.binderSessions[0]);
								this.isDataLoading = false;
							}
							else {
								this.handleBinderAccessOfNonRegisteredRole();
							}
						});
				}
				else {
					this.handleBinderAccessOfNonRegisteredRole();
				}
			}

			
		}, (error) => {
			this.isDataLoading = false;
		});
	}
	handleBinderAccessOfNonRegisteredRole() {
		if (!!this.binderWizardDetails.isBinderAndCustomerLegacy) {
			this.submitBinderQuery(null);
		} else {
			if (this.binderFormGroup.get('hasWorkToDo').value) {
				if (!this.binderWizardDetails.hasEnhancedSecurity
					|| !this.binderWizardDetails.allowAutoRegistration) {
					this.publicViewingMessage = 'DENIED';
					this.errorMessage = this.publicAccessWarningMessage;
					this.jumpToSection(SectionsEnum.Error);
					this.isDataLoading = false;
				}
				if (this.binderWizardDetails.hasEnhancedSecurity
					&& this.binderWizardDetails.allowAutoRegistration) {
					if (this.binderWizardDetails.bypassCustomerWhitelist || this.isBinderEmailAllowed) {
						this.submitBinderQuery(null);
					} else if (!this.binderWizardDetails.bypassCustomerWhitelist && !this.isBinderEmailAllowed) {
						this.publicViewingMessage = null;
						this.errorMessage = this.autoRegisterWarningMessage;
						this.jumpToSection(SectionsEnum.Error);
						this.isDataLoading = false;
					}
				}
			} else {
				if (!this.binderWizardDetails.limitAccessToCustomerWhitelist && this.binderWizardDetails.isLimitedPublicView) {
					this.publicViewingMessage = 'DENIED';
					this.errorMessage = this.publicAccessWarningMessage;
					this.jumpToSection(3);
					this.isDataLoading = false;
				} else {
					this.submitBinderQuery(null);
				}
			}
		}
	}
	public submitBinderQuery(account?: BinderUserAccessAccount): void {
		if (this.binderFormGroup.invalid) {
			return;
		}
		const model = this.binderFormGroup.getRawValue();

		this.isDataLoading = true;

		this.binderWizardService.submitBinderQuery(TokenHelper.getToken(this.activatedRoute),
			{
				email: model.email.trim(),
				hasWorkToDo: model.hasWorkToDo,
				userAccountId: !!account ? account?.userAccountId : null,
				isChannelPartner: !!account ? account.isChannelPartner : false
			})
			.subscribe(() => {
				localStorage.setItem('cloud-email', model.email.trim());
				this.jumpToSection(2);
				this.isDataLoading = false;
			}, (error) => {
				if (error.status === 400 && error.error?.isVendorMissig === true) {
					this.publicViewingMessage = null;
					this.errorMessage = this.autoRegisterWarningMessage;
					this.jumpToSection(SectionsEnum.Error);
				}
				this.isDataLoading = false;
			});
	}

	public selectNextSection(): void {
		const currentSection = this.currentlyVisibleSection;
		this.sections[currentSection + 1] = true;
	}

	public selectPreviousSection(unhideIfHidden: boolean = false): void {
		const currentSection = this.currentlyVisibleSection;

		if (unhideIfHidden) {
			this.sections[currentSection - 1] = true;
			this.sections[currentSection] = false;
		} else {
			this.sections[currentSection] = false;
		}
	}

	public jumpToSection(sectionNumber: number): void {
		this.sections[sectionNumber] = true;

		const currentSection = this.currentlyVisibleSection;

		if (sectionNumber < currentSection) {
			for (let index = sectionNumber + 1; index < this.sections.length; ++index) {
				this.sections[index] = false;
			}
		}
	}

	public onChangeSite() {
		this.router.navigate(['binder']);
	}

	public backToBinderRequest(): void {
		this.isBinderEmailAllowed = false;
		this.accessToken = '';
		this.binderWizardService.getBinderWizardDetails(TokenHelper.getToken(this.activatedRoute)).subscribe(res => {
			this.binderWizardDetails = res;
			this.jumpToSection(0);
		});
	}

	private hasWorkToDoFlow(): void {
		if (this.binderWizardDetails.hasEnhancedSecurity) {
			if (this.binderWizardDetails.isGeoFencingRequired) {
				this.locationService
					.getLocation()
					.subscribe(
						result => {
							this.isDataLoading = false;
							if (result && result.coords) {
								this.checkIsGeoFancingValid(result.coords.latitude, result.coords.longitude);
							}
						},
						err => {
							this.isDataLoading = false;
							this.checkIsGeoFencingTokenEnabled();
						}
					);
			} else {
				if (this.binderWizardDetails.allowTokenAuthentication) {
					this.jumpToSection(4);
					this.isTokenEnabledFlow = true;
					this.isDataLoading = false;
				} else {
					this.getUserAccountsData();
				}
			}
		} else {
			this.getUserAccountsData();
		}
	}

	private checkIsGeoFancingValid(lat, lon) {
		const distance = this.locationService.getDistace(lat, lon, this.binderWizardDetails.latitude, this.binderWizardDetails.longitude);

		if (distance <= this.binderWizardDetails.defaultGeoFencingRadius) {
			this.getUserAccountsData();
		}
		else {
			this.checkIsGeoFencingTokenEnabled();
		}
	}

	private checkIsGeoFencingTokenEnabled() {
		this.isGeoFencingValidationFailed = true;

		if (this.binderWizardDetails.tokenAuthOnlyOnFailedGeoFencing && this.binderWizardDetails.hasCustomerSupportTokenIssuer) {
			this.checkCustomerSupportToken();
		}
		else {
			this.hasGeoFencingTokenDisabled = true;
		}

		this.jumpToSection(4);
	}

	public checkCustomerSupportToken() {
		if (this.accessToken) {
			this.binderWizardService
				.checkAccessToken(this.binderWizardDetails.virtualBinderHashId, this.binderFormGroup.get('email').value, this.accessToken)
				.subscribe(
					result => {
						if (result) {
							this.isGeoFencingTokenValid = true;
							this.getUserAccountsData();
						} else {
							// geo fencing token is not valid
							this.isGeoFencingTokenValid = false;
							this.jumpToSection(5);
						}
					},
					() => {
						// geo fencing token is not valid
						this.isGeoFencingTokenValid = false;
						this.jumpToSection(5);
					}
				);
		}
	}
	onBinderSessionClick(binderSession: BinderSessionViewByEmail) {
		this.NavigateToBinderSession(binderSession);
	}
	
	private NavigateToBinderSession(binderSession: BinderSessionViewByEmail) {
		const model = this.binderFormGroup.getRawValue();
		const email = model.email.trim();
		localStorage.setItem('cloud-email', email);

		// if the binder session is not domain specific, then navigate to the binder session directly
		if (!binderSession.isRestrictedDomainSpecific) {
			this.router.navigate(
				[binderSession.redirectUri]
			);
		}
		else {
			// if the binder session is domain specific, then generate a token for the email and domain
			// and navigate to the binder session with the new token
			this.isDataLoading = true;
			this.binderWizardService.submitBinderSessionAccessRequest(
				TokenHelper.getToken(this.activatedRoute),
				email,
				binderSession.binderSessionId
			).subscribe(
				(res) => {
					if (res && res.redirectUri) {
						this.isDataLoading = false;
						this.router.navigate([res.redirectUri]);
					}
					else {
						this.isDataLoading = false;
						this.errorMessage = 'Error occurred while navigating to the binder session.';
						this.publicViewingMessage = 'FAILED';
						this.jumpToSection(SectionsEnum.Error);
					}
				},
				(err) => {
					this.isDataLoading = false;
					this.errorMessage = err.error?.message || 'Error occurred while navigating to the binder session.';
					this.publicViewingMessage = 'FAILED';
					this.jumpToSection(SectionsEnum.Error);
				}
			);
		}
	}
}
